import React, { useEffect, useState } from "react";
import Loading from "../pages/Loading";
import ConfirmToast from "../pages/Toast";
import NotificationToast from "../Component/Toast";
import Cookies from "js-cookie";
import PaginationFooter from "./PaginationFooter";
import { AddFileModal } from "./Modals";
import { preview_files } from "../apis/PreviewFile";
import { get_file_by_id } from "../apis/GetFileById";
import SendEmailModal from "./SendEmailModal";
import { get_files_list } from "../apis/GetFIlesList";
import { add_files } from "../apis/AddFiles";
import { modify_files } from "../apis/ModifyFiles";
import { delete_file } from "../apis/DeleteFile";
import { modify_file_status } from "../apis/ModifyFileStatus";
import { setAPIAuthorizationToken } from "../apis/API";
import "../css/files.scss";
import SelectOption from "./SelectOption";
import { useTranslation } from "../contexts/LocaleContext";

const Files = () => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toastInfo, setToastInfo] = useState({ message: "", type: "" });
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendTemplateData, setSendTemplateData] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [goToInput, setGoToInput] = useState("1");
  const [statusFilter, setStatusFilter] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const usertype = Number(Cookies.get("usertype"));

  const fetchFiles = async (
    page = 1,
    suppressLoading = false,
    name = "",
    perPage = pageSize,
    status = statusFilter
  ) => {
    try {
      if (!suppressLoading) setIsLoading(true);
      const res = await get_files_list(page, perPage, name, status);
      let items = [];
      let total = 0;
      if (res && res.data != null) {
        if (Array.isArray(res.data)) {
          items = res.data;
          total = items.length;
        } else if (res.data && Array.isArray(res.data.data)) {
          items = res.data.data;
          total =
            (res.data && (res.data.total ?? items.length)) || items.length;
        } else if (res.data && Array.isArray(res.data.items)) {
          items = res.data.items;
          total =
            (res.data && (res.data.total ?? items.length)) || items.length;
        } else {
          // unexpected shape (string error message, null, etc.)
          console.warn("Unexpected files API response shape:", res);
          items = [];
          total = 0;
        }
      }
      setFiles(items);
      setTotalItems(Number(total) || items.length);
    } catch (err) {
      console.error("Failed to fetch files:", err);
      setToastInfo({ message: "Failed to load files", type: "error" });
    } finally {
      if (!suppressLoading) setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = Cookies.get("token");
    if (token) setAPIAuthorizationToken(token);
    fetchFiles(1, false, "", pageSize, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setGoToInput(String(currentPage));
  }, [currentPage]);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const goToPage = async (page) => {
    const p = Math.max(1, Math.min(totalPages, Number(page) || 1));
    setCurrentPage(p);
    await fetchFiles(p, false, searchName, pageSize, statusFilter);
  };

  const openEditModal = async (file) => {
    const id = file?.id ?? file?._id ?? file;
    if (!id) {
      setEditingFile(file || null);
      setShowFileModal(true);
      return;
    }
    try {
      setIsLoading(true);
      const res = await get_file_by_id(id);
      const data = res && res.data ? res.data : null;
      const item = data && data.data ? data.data : data;
      setEditingFile(item || null);
      setShowFileModal(true);
    } catch (err) {
      console.error("Failed to fetch file by id:", err);
      showNotification("Failed to load file details", "error");
    } finally {
      setIsLoading(false);
    }
  };
  const handlePreview = async (file) => {
    const id = file?.id ?? file?._id ?? file;
    if (!id) {
      showNotification("Cannot preview: missing id", "error");
      return;
    }
    try {
      setIsLoading(true);
      const res = await preview_files(
        id,
        "{name},{img},{link}",
        "",
        "{name}",
        ""
      );
      const data = res && res.data ? res.data : null;
      const body = data && data.data ? data.data : data;
      if (!body) {
        showNotification("No preview data returned", "error");
        return;
      }

      // prefer explicit content or content field from API; fallback to stringified body
      let html = "";
      if (typeof body === "string") {
        html = body;
      } else if (body.content) {
        html = body.content;
      } else if (body.previewHtml) {
        html = body.previewHtml;
      } else if (body.previewUrl || body.url) {
        // if it's a URL, open in new tab as fallback
        window.open(body.previewUrl || body.url, "_blank");
        showNotification("Preview opened in new tab", "success");
        return;
      } else {
        html = `<pre>${JSON.stringify(body, null, 2)}</pre>`;
      }

      // open send modal with template data for user to fill recipient/replace values
      // pass merged data: original file fields overridden by preview response
      setSendTemplateData({
        ...(file || {}),
        ...(body || {}),
        id: body.id ?? body._id ?? id,
        subject: body.subject ?? file?.subject ?? "",
        content: html,
        locating: body.locating ?? body.subjectlocating ?? file?.locating ?? "",
      });
      setSendModalOpen(true);
    } catch (err) {
      console.error("Failed to preview file:", err);
      showNotification("Failed to preview file", "error");
    } finally {
      setIsLoading(false);
    }
  };
  const closeFileModal = () => {
    setShowFileModal(false);
    setEditingFile(null);
  };

  const showNotification = (message, type = "success") =>
    setToastInfo({ message, type });

  const handleSubmitFile = async (form) => {
    setIsSaving(true);
    try {
      if (form.id) {
        const updateTime = new Date().toISOString();
        await modify_files(
          form.id,
          form.subject,
          form.content,
          form.status,
          form.filename,
          updateTime,
          form.locating,
          form.subjectlocating
        );
        showNotification("File updated", "success");
      } else {
        await add_files(
          form.subject,
          form.content,
          form.status,
          form.filename,
          form.locating,
          form.subjectlocating
        );
        showNotification("File added", "success");
      }
      await fetchFiles(currentPage, true, searchName, pageSize, statusFilter);
      closeFileModal();
    } catch (err) {
      console.error("Failed to save file:", err);
      showNotification("Failed to save file", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleFileStatus = async (file) => {
    const apiId = file.id ?? file._id;
    if (!apiId) {
      showNotification("Cannot change status: missing id", "error");
      return;
    }

    const newStatus = file.status === "1" ? "0" : "1";

    // optimistic update
    const prevFiles = files;
    setFiles((cur) =>
      cur.map((f) =>
        f.id === apiId || f._id === apiId ? { ...f, status: newStatus } : f
      )
    );

    try {
      setIsSaving(true);
      await modify_file_status(apiId, newStatus);
      showNotification("Status updated", "success");
    } catch (err) {
      console.error("Failed to update status:", err);
      // revert
      setFiles(prevFiles);
      showNotification("Failed to update status", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="users-page">
      <NotificationToast
        message={toastInfo.message}
        type={toastInfo.type}
        onClose={() => setToastInfo({ message: "", type: "" })}
      />
      {isSaving && (
        <div className="full-screen-overlay" aria-hidden="true">
          <Loading />
        </div>
      )}

      <div className="users-controls-card">
        <div className="controls-left">
          <div className="search-input">
            <input
              placeholder={t ? t("files.search_placeholder") : "Search by name"}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  fetchFiles(1, false, searchName, pageSize, statusFilter);
                }
              }}
            />
            <button
              type="button"
              className="search-icon"
              aria-label="Search"
              onClick={() => {
                setCurrentPage(1);
                fetchFiles(1, false, searchName, pageSize, statusFilter);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="m16.875 15.455 4 4.005a.5.5 0 0 1-.02.691l-.7.7a.5.5 0 0 1-.71 0l-4-4.004a2.6 2.6 0 0 1-.3-.36l-.75-1.001a7 7 0 0 1-4.37 1.531A7.011 7.011 0 0 1 6.973 3.69a6.995 6.995 0 0 1 8.518 1.971 7.015 7.015 0 0 1-.046 8.753l1 .691q.234.151.43.35M5.025 10.01a5.003 5.003 0 0 0 5 5.006 5 5 0 0 0 3.535-1.466 5 5 0 0 0 1.465-3.54 5.003 5.003 0 0 0-5-5.006c-2.761 0-5 2.241-5 5.006"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div className="status-select-wrap">
            <SelectOption
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
                fetchFiles(1, false, searchName, pageSize, v);
              }}
              options={[
                { value: "", label: t ? t("filter.all") : "All" },
                { value: "1", label: t ? t("status.on") : "ON" },
                { value: "0", label: t ? t("status.off") : "OFF" },
              ]}
              ariaLabel="状态筛选"
            />
          </div>
        </div>
        {usertype === 2 && (
          <div className="controls-right">
            <button className="btn-add" onClick={() => setShowFileModal(true)}>
              {t ? t("files.add") : "Add file"}
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <Loading />
      ) : files.length === 0 ? (
        <div>{t ? t("files.no_items") : "No files"}</div>
      ) : (
        <div className="users-table-card">
          <div className="table-scroll">
            <table className="users-table">
              <thead>
                <tr>
                  <th>{t ? t("files.table.filename") : "Filename"}</th>
                  <th>{t ? t("files.table.status") : "Status"}</th>
                  <th>{t ? t("files.table.date") : "Date"}</th>
                  <th>{t ? t("files.table.operation") : "Operation"}</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f, idx) => {
                  const id = f.id ?? f._id ?? idx;
                  return (
                    <tr key={id}>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-value">
                            {f.filename ?? "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("files.table.status") : "Status"}
                          </span>
                          <span className="cell-value">
                            {usertype === 2 ? (
                              <>
                                {f.status ? (
                                  <button
                                    type="button"
                                    className={
                                      f.status === "1"
                                        ? "status-switch on"
                                        : "status-switch"
                                    }
                                    onClick={() => toggleFileStatus(f)}
                                    disabled={isSaving}
                                    aria-label="Toggle status"
                                  >
                                    <span className="switch-track" />
                                    <span className="switch-knob" />
                                  </button>
                                ) : (
                                  "-"
                                )}
                              </>
                            ) : (
                              <>
                                {f.status ? (
                                  <span
                                    className={
                                      f.status === "1"
                                        ? "status-pill status-available"
                                        : "status-pill status-off"
                                    }
                                  >
                                    {t
                                      ? f.status === "1"
                                        ? t("status.on")
                                        : t("status.off")
                                      : f.status === "1"
                                      ? "ON"
                                      : "OFF"}
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </>
                            )}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-label">
                            {t ? t("files.table.date") : "Date"}
                          </span>
                          <span className="cell-value">
                            {f.updateTime
                              ? new Date(f.updateTime).toLocaleString()
                              : "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="cell-with-label">
                          <span className="cell-value">
                            <div className="op-buttons">
                              <button
                                className="btn-edit"
                                onClick={() => handlePreview(f)}
                              >
                                <svg
                                  t="1767773573297"
                                  class="icon"
                                  viewBox="0 0 1024 1024"
                                  version="1.1"
                                  xmlns="http://www.w3.org/2000/svg"
                                  p-id="22207"
                                  width="200"
                                  height="200"
                                >
                                  <path
                                    d="M515.413333 610.986667c-23.893333 0-47.786667-6.826667-68.266666-23.893334l-61.44-51.2c-13.653333-13.653333-17.066667-34.133333-3.413334-47.786666 13.653333-13.653333 34.133333-17.066667 47.786667-3.413334l61.44 51.2c13.653333 10.24 30.72 10.24 44.373333 0L853.333333 290.133333H204.8c-20.48 0-34.133333-13.653333-34.133333-34.133333s13.653333-34.133333 34.133333-34.133333h648.533333c30.72 0 54.613333 17.066667 64.853334 47.786666 10.24 27.306667 0 58.026667-23.893334 78.506667l-317.44 245.76c-17.066667 10.24-40.96 17.066667-61.44 17.066667zM921.6 733.866667v-273.066667c0-20.48-13.653333-34.133333-34.133333-34.133333s-34.133333 13.653333-34.133334 34.133333v273.066667c0 20.48-13.653333 34.133333-34.133333 34.133333H341.333333c-20.48 0-34.133333 13.653333-34.133333 34.133333s13.653333 34.133333 34.133333 34.133334h477.866667c58.026667 0 102.4-47.786667 102.4-102.4z m-546.133333-341.333334c0-20.48-13.653333-34.133333-34.133334-34.133333H136.533333c-20.48 0-34.133333 13.653333-34.133333 34.133333s13.653333 34.133333 34.133333 34.133334h204.8c20.48 0 34.133333-17.066667 34.133334-34.133334z m0 273.066667c0-20.48-13.653333-34.133333-34.133334-34.133333H238.933333c-20.48 0-34.133333 13.653333-34.133333 34.133333s13.653333 34.133333 34.133333 34.133333h102.4c20.48 0 34.133333-17.066667 34.133334-34.133333z"
                                    fill="#000000"
                                    p-id="22208"
                                  ></path>
                                </svg>
                              </button>

                              {usertype === 2 && (
                                <>
                                  <button
                                    className="btn-edit"
                                    onClick={() => {
                                      openEditModal(f);
                                    }}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="1em"
                                      height="1em"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        fill="#000"
                                        fillRule="evenodd"
                                        d="m19.902 3.44.659.658c.28.28.439.66.439 1.058v.758c.005.404-.153.793-.44 1.078l-1.845 1.816a.5.5 0 0 1-.708 0l-2.804-2.844a.5.5 0 0 1 0-.699L17.01 3.44A1.5 1.5 0 0 1 18.087 3h.758c.397 0 .777.158 1.057.44m-3.082 7.245a.5.5 0 0 0 0-.7l-2.854-2.813a.5.5 0 0 0-.708 0l-8.052 8.053a2 2 0 0 0-.449.679l-1.716 4.331a.5.5 0 0 0 .19.619.5.5 0 0 0 .538.11l4.33-1.757c.255-.101.486-.254.679-.449z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                  <button
                                    className="btn-delete"
                                    onClick={() => setConfirmDelete(id)}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="1em"
                                      height="1em"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        fill="#000"
                                        fillRule="evenodd"
                                        d="M20 5.5v-1a.5.5 0 0 0-.5-.5H15V3a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v1H4.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h15a.5.5 0 0 0 .5-.5M7.72 18h8.56L17 8h2l-.85 12.14a2 2 0 0 1-2 1.86H7.87a2 2 0 0 1-2-1.86L5 8h2z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </button>
                                </>
                              )}
                            </div>
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="users-table-footer" style={{ marginTop: 12 }}>
            <PaginationFooter
              pageSize={pageSize}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setCurrentPage(1);
                fetchFiles(1, false, searchName, n, statusFilter);
              }}
              currentPage={currentPage}
              onPageChange={(p) => goToPage(p)}
              totalPages={totalPages}
              goToValue={goToInput}
              onGoToValueChange={(v) => setGoToInput(v)}
              onGoClick={() => goToPage(goToInput)}
            />
          </div>
        </div>
      )}

      <AddFileModal
        isOpen={showFileModal}
        onClose={closeFileModal}
        initialData={editingFile || undefined}
        onSubmit={handleSubmitFile}
        isLoading={isSaving}
      />
      <SendEmailModal
        isOpen={sendModalOpen}
        onClose={() => {
          setSendModalOpen(false);
          setSendTemplateData(null);
        }}
        templateData={sendTemplateData}
        onSent={(ok) => {
          if (ok)
            showNotification(
              t ? t("notification.email_added") : "Email sent",
              "success"
            );
          else
            showNotification(
              t ? t("notification.failed_save") : "Failed to send email",
              "error"
            );
        }}
      />
      {showPreviewModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setShowPreviewModal(false);
            setPreviewHtml("");
          }}
        >
          <div
            className="modal-card"
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{ maxWidth: "90%", width: 900, padding: 12 }}
          >
            <div className="modal-header">
              <h3>Preview</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewHtml("");
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div
              className="modal-body"
              style={{ maxHeight: "70vh", overflow: "auto", padding: 16 }}
            >
              <div
                className="preview-content"
                dangerouslySetInnerHTML={{ __html: previewHtml || "" }}
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmToast
        show={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        confirmProps={{
          title: t ? t("confirm.delete_file.title") : "Delete File",
          description: confirmDelete
            ? t
              ? t("confirm.delete_file.description")
              : `Are you sure you want to delete this file?`
            : "",
          confirmLabel: t ? t("confirm.delete") : "Delete",
          cancelLabel: t ? t("confirm.cancel") : "Cancel",
          onConfirm: async () => {
            try {
              setIsSaving(true);
              if (confirmDelete) {
                await delete_file(confirmDelete);
                showNotification(
                  t ? t("notification.file_deleted") : "File deleted",
                  "success"
                );
                await fetchFiles(
                  currentPage,
                  false,
                  searchName,
                  pageSize,
                  statusFilter
                );
              }
            } catch (err) {
              console.error("Failed to delete file:", err);
              showNotification(
                t
                  ? t("notification.file_delete_failed")
                  : "Failed to delete file",
                "error"
              );
            } finally {
              setIsSaving(false);
              setConfirmDelete(null);
            }
          },
          onCancel: () => setConfirmDelete(null),
        }}
      />

      <NotificationToast
        message={toastInfo.message}
        type={toastInfo.type}
        onClose={() => setToastInfo({ message: "", type: "" })}
      />
    </div>
  );
};

export default Files;
